// src/components/Authorizations/AuthorizationCard.js

import React, { useCallback } from "react";
import classNames from "classnames";
import {
  Card,
  Typography,
  Row,
  Col,
  Button,
  Tooltip,
  Tag,
  Space,
  Alert,
  Badge,
} from "antd";
import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  CarOutlined,
  PlusCircleOutlined,
  HistoryOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ProfileOutlined,
  BranchesOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import PropTypes from "prop-types";
import "./AuthorizationCard.css";

const { Text } = Typography;
const limpiarUbicacion = valor => {
  if (valor == null) return "";
  const texto = String(valor).trim();
  return /^(null|undefined)?$/i.test(texto) ? "" : texto;
};

/**
 * Helper: Devuelve la clase de borde según el estado.
 */
const getBorderClass = (estado) => {
  if (!estado) return "border-default";
  switch (estado.toLowerCase()) {
    case "activa":
      return "border-activa";
    case "suspendida":
      return "border-suspendida";
    case "completada":
      return "border-completada";
    default:
      return "border-default";
  }
};

/**
 * AuthorizationCard
 *
 * Este componente muestra la información de una autorización en forma de tarjeta.
 * Recibe varias props para manejar eventos (editar, ver, eliminar, etc.)
 */
const AuthorizationCard = React.memo(
  ({
    authorization,
    isSelected,
    onSelect,
    onEdit,
    onView,
    onDelete,
    onCreateNovedad,
    onHistorialNovedades,
    getBorderColor,
    onHistorialAutorizacion,
    onVersiones,
    onObservacionesAutorizacion,
    hasPermission,
  }) => {
    // Extraer campos de la autorización
    const {
      id,
      estado_autorizacion,
      numero_autorizacion,
      numero_pre_autorizacion,
      codigo_mipres,
      origen_direccion,
      origen_institucion,
      origen_lugar_temporal,
      destino_institucion,
      destino_lugar_temporal,
      origen_ciudad,
      origen_departamento,
      destino_direccion,
      destino_ciudad,
      destino_departamento,
      inicio_fecha_autorizacion,
      final_fecha_autorizacion,
      nombre_paciente,
      tipo_documento_paciente,
      numero_documento_paciente,
      telefono_responsable,
      empresa,
      observaciones,
      tipo_traslado,
      tipo_viaje,
      copago,
      copago_diario,
      estado_secundario,
      estado_terciario,
      estado_cuarto,
      numero_entrega,
      entregas_autorizadas,
      fallidos,
      cancelados,
      es_tai,
      tai_tipo_transporte,
    } = authorization;

    // Definir la clase del card utilizando classNames
    const cardClass = classNames("authorization-card", {
      selected: isSelected,
      [getBorderClass(estado_autorizacion)]: true,
    });

    // Función para formatear números en moneda COP
    const formatCurrency = (value) => {
      if (value === undefined || value === null) return "N/A";
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
      }).format(value);
    };

    const origenTemporal = limpiarUbicacion(origen_lugar_temporal);
    const destinoTemporal = limpiarUbicacion(destino_lugar_temporal);
    const origenNombre = origenTemporal || limpiarUbicacion(origen_institucion);
    const destinoNombre = destinoTemporal || limpiarUbicacion(destino_institucion);
    const origenDetalle = [origen_direccion, origen_ciudad, origen_departamento].map(limpiarUbicacion).filter(Boolean).join(", ");
    const destinoDetalle = [destino_direccion, destino_ciudad, destino_departamento].map(limpiarUbicacion).filter(Boolean).join(", ");
    const origenSufijo = origenNombre && origenDetalle ? `, ${origenDetalle}` : "";
    const destinoSufijo = destinoNombre && destinoDetalle ? `, ${destinoDetalle}` : "";
    const getOrigenText = () => origenNombre || "Origen no especificado";
    const getDestinoText = () => destinoNombre || "Destino no especificado";
    const getOrigenTooltip = () => `${getOrigenText()}${origenSufijo}${origenTemporal ? " (Lugar temporal)" : ""}`;
    const getDestinoTooltip = () => `${getDestinoText()}${destinoSufijo}${destinoTemporal ? " (Lugar temporal)" : ""}`;

    // Memorizar handlers para evitar recreaciones
    const handleHistorialAutorizacion = useCallback(
      (e) => {
        e.stopPropagation();
        onHistorialAutorizacion(id);
      },
      [onHistorialAutorizacion, id]
    );

    const handleEdit = useCallback(
      (e) => {
        e.stopPropagation();
        onEdit(authorization);
      },
      [onEdit, authorization]
    );

    const handleView = useCallback(
      (e) => {
        e.stopPropagation();
        onView(id);
      },
      [onView, id]
    );

    const handleDelete = useCallback(
      (e) => {
        e.stopPropagation();
        onDelete(id);
      },
      [onDelete, id]
    );

    const handleCreateNovedad = useCallback(
      (e) => {
        e.stopPropagation();
        onCreateNovedad(authorization);
      },
      [onCreateNovedad, authorization]
    );

    const handleHistorialNovedades = useCallback(
      (e) => {
        e.stopPropagation();
        onHistorialNovedades(id);
      },
      [onHistorialNovedades, id]
    );

    const handleVersiones = useCallback(
      (e) => {
        e.stopPropagation();
        onVersiones(id);
      },
      [onVersiones, id]
    );

    const handleObservacionesAutorizacion = useCallback(
      (e) => {
        e.stopPropagation();
        onObservacionesAutorizacion(id);
      },
      [onObservacionesAutorizacion, id]
    );

    // Al hacer clic en la tarjeta, si no hay selección de texto, se activa la función onSelect
    const handleCardClick = (e) => {
      const selection = window.getSelection();
      if (!selection || selection.toString().length === 0) {
        onSelect(id);
      }
    };

    // Función para definir el color de los tags según el estado (opcional)
    const getStatusTagColor = (estado) => {
      if (!estado) return "default";
      switch (estado.toLowerCase()) {
        case "activa":
          return "geekblue";
        case "suspendida":
          return "volcano";
        case "completada":
          return "green";
        case "cerrada":
          return "purple";
        default:
          return "default";
      }
    };

    return (
      <Card className={cardClass} onClick={handleCardClick} hoverable>
        <Row gutter={[8, 8]} className="card-row">
          {/* Información de Autorización (Número, MIPRES, etc.) */}
          <Col xs={24} sm={8}>
            <div className="authorization-header">
              {numero_autorizacion && (
                <div className="authorization-info">
                  <FileTextOutlined
                    className="authorization-icon"
                    style={{ color: "#cccccc" }}
                  />
                  <strong>AUT:</strong>
                  <Text ellipsis={{ tooltip: numero_autorizacion }}>
                    {numero_autorizacion}
                  </Text>
                </div>
              )}
              {numero_pre_autorizacion && (
                <div className="authorization-info">
                  <FileTextOutlined
                    className="authorization-icon"
                    style={{ color: "#cccccc" }}
                  />
                  <strong>PRE:</strong>
                  <Text ellipsis={{ tooltip: numero_pre_autorizacion }}>
                    {numero_pre_autorizacion}
                  </Text>
                </div>
              )}
              {codigo_mipres && (
                <div className="authorization-info">
                  <FileTextOutlined
                    className="authorization-icon"
                    style={{ color: "#cccccc" }}
                  />
                  <strong>MIPRES:</strong>
                  <Text ellipsis={{ tooltip: codigo_mipres }}>
                    {codigo_mipres}
                  </Text>
                </div>
              )}
            </div>
          </Col>

          {/* Información de Origen y Destino */}
          <Col xs={24} sm={8}>
            <div className="authorization-info">
              <EnvironmentOutlined
                className="authorization-icon"
                style={{ color: origenNombre ? "green" : "#cccccc" }}
              />
              <Text
                ellipsis={{
                  tooltip: getOrigenTooltip(),
                }}
              >
                <span style={{ fontWeight: "bold" }}>
                  {getOrigenText()}
                </span>
                {origenSufijo}
              </Text>
            </div>
            <div className="authorization-info">
              <EnvironmentOutlined
                className="authorization-icon"
                style={{ color: destinoNombre ? "red" : "#cccccc" }}
              />
              <Text
                ellipsis={{
                  tooltip: getDestinoTooltip(),
                }}
              >
                <span style={{ fontWeight: "bold" }}>
                  {getDestinoText()}
                </span>
                {destinoSufijo}
              </Text>
            </div>
            <div className="authorization-info">
              <CalendarOutlined
                className="authorization-icon"
                style={{ color: "#cccccc" }}
              />
              <Text>
                {new Date(inicio_fecha_autorizacion).toLocaleDateString()} -{" "}
                {new Date(final_fecha_autorizacion).toLocaleDateString()}
              </Text>
            </div>
          </Col>

          {/* Información del Paciente */}
          <Col xs={24} sm={8}>
            <div className="authorization-info right-aligned">
              <UserOutlined
                className="authorization-icon"
                style={{ color: "#cccccc" }}
              />
              <Text ellipsis={{ tooltip: nombre_paciente }}>
                {nombre_paciente}
              </Text>
              {hasPermission("PREVISUALIZAR_A") && (
                <Badge
                  count={authorization.observaciones_count || 0}
                  style={{
                    backgroundColor: (authorization.observaciones_count || 0) > 0 ? "#13c2c2" : "#d9d9d9",
                    boxShadow: (authorization.observaciones_count || 0) > 0
                      ? "0 0 0 2px rgba(19, 194, 194, 0.2)"
                      : "0 0 0 2px rgba(217, 217, 217, 0.2)",
                    cursor: "pointer",
                    marginLeft: 16,
                  }}
                  title={`${authorization.observaciones_count || 0} observaciones`}
                >
                  <CommentOutlined
                    style={{
                      fontSize: "16px",
                      color: (authorization.observaciones_count || 0) > 0 ? "#13c2c2" : "#8c8c8c",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}
                    onClick={handleObservacionesAutorizacion}
                  />
                </Badge>
              )}
            </div>
            {tipo_documento_paciente && (
              <div className="authorization-info right-aligned">
                <IdcardOutlined
                  className="authorization-icon"
                  style={{ color: "#cccccc" }}
                />
                <Text>{tipo_documento_paciente}</Text>
              </div>
            )}
            {numero_documento_paciente && (
              <div className="authorization-info right-aligned">
                <IdcardOutlined
                  className="authorization-icon"
                  style={{ color: "#cccccc" }}
                />
                <Text>{numero_documento_paciente}</Text>
              </div>
            )}
            <div className="authorization-info right-aligned">
              <PhoneOutlined
                className="authorization-icon"
                style={{ color: "#cccccc" }}
              />
              <Text>{telefono_responsable}</Text>
            </div>
            <div className="authorization-info right-aligned">
              <MedicineBoxOutlined
                className="authorization-icon"
                style={{ color: "#cccccc" }}
              />
              <Text>{empresa}</Text>
            </div>
          </Col>
        </Row>

        {/* Estado y Copago */}
        <Row
          justify="space-between"
          align="middle"
          className="authorization-footer"
        >
          <Col>
            <Tag
              color={getStatusTagColor(estado_autorizacion)}
              className="status-tag"
            >
              {estado_autorizacion}
            </Tag>
            {estado_secundario && (
              <Tag
                color={getStatusTagColor(estado_autorizacion)}
                className="status-tag"
              >
                {estado_secundario}
              </Tag>
            )}
            {estado_terciario && (
              <Tag
                color={getStatusTagColor(estado_autorizacion)}
                className="status-tag"
              >
                {estado_terciario}
              </Tag>
            )}
            {estado_cuarto && (
              <Tag
                color={getStatusTagColor(estado_autorizacion)}
                className="status-tag"
              >
                {estado_cuarto}
              </Tag>
            )}
            {fallidos > 0 && <Tag color="brown">{fallidos}</Tag>}
            {cancelados > 0 && <Tag color="red">{cancelados}</Tag>}
            {es_tai && (
              <Tooltip title={`Autorización TAI - ${tai_tipo_transporte || 'Sin tipo'}`}>
                <Tag color="purple" icon={<CarOutlined />}>
                  TAI
                </Tag>
              </Tooltip>
            )}
          </Col>

          <Col>
            <Space>
              {entregas_autorizadas !== undefined &&
                entregas_autorizadas !== null && (
                  <Tag color={getStatusTagColor(estado_autorizacion)}>
                    A - {entregas_autorizadas}
                  </Tag>
                )}
              {numero_entrega !== undefined && numero_entrega !== null && (
                <Tag color={getStatusTagColor(estado_autorizacion)}>
                  E - {numero_entrega}
                </Tag>
              )}
              <Tag
                icon={<CarOutlined />}
                color={getStatusTagColor(estado_autorizacion)}
              >
                {tipo_traslado} - {tipo_viaje}
              </Tag>
              {copago !== undefined && copago !== null && (
                <Tag icon={<DollarOutlined />} color="blue">
                  T {formatCurrency(copago)}
                </Tag>
              )}
              {copago_diario !== undefined && copago_diario !== null && (
                <Tag icon={<DollarOutlined />} color="orange">
                  S {formatCurrency(copago_diario)}
                </Tag>
              )}
            </Space>
          </Col>
        </Row>

        {observaciones && (
          <Row>
            <Col span={24}>
              <Alert
                description={observaciones}
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                className="observations-alert"
              />
            </Col>
          </Row>
        )}

        {isSelected && (
          <Row justify="end" className="authorization-actions">
            {hasPermission("PREVISUALIZAR_A") && (
              <Tooltip title="Historial de Autorización">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<ProfileOutlined />}
                  size="small"
                  onClick={handleHistorialAutorizacion}
                />
              </Tooltip>
            )}
            {hasPermission("PREVISUALIZAR_A") && (
              <Tooltip title="Versiones Anteriores">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<BranchesOutlined />}
                  size="small"
                  style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
                  onClick={handleVersiones}
                />
              </Tooltip>
            )}
            {hasPermission("EDITAR_A") && (
              <Tooltip title="Editar">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={handleEdit}
                />
              </Tooltip>
            )}
            {hasPermission("PREVISUALIZAR_A") && (
              <Tooltip title="Ver">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={handleView}
                />
              </Tooltip>
            )}
            {hasPermission("ELIMINAR_A") && (
              <Tooltip title="Eliminar">
                <Button
                  type="primary"
                  shape="circle"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                  onClick={handleDelete}
                />
              </Tooltip>
            )}
            {hasPermission("EDITAR_A") && (
              <Tooltip title="Crear Novedad">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusCircleOutlined />}
                  size="small"
                  onClick={handleCreateNovedad}
                />
              </Tooltip>
            )}
            {hasPermission("PREVISUALIZAR_A") && (
              <Tooltip title="Historial de Novedades">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<HistoryOutlined />}
                  size="small"
                  onClick={handleHistorialNovedades}
                />
              </Tooltip>
            )}
            {hasPermission("PREVISUALIZAR_A") && (
              <Tooltip title="Observaciones">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<CommentOutlined />}
                  size="small"
                  style={{ backgroundColor: "#13c2c2", borderColor: "#13c2c2" }}
                  onClick={handleObservacionesAutorizacion}
                />
              </Tooltip>
            )}
          </Row>
        )}
      </Card>
    );
  }
);

AuthorizationCard.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.number.isRequired,
    estado_autorizacion: PropTypes.string.isRequired,
    numero_autorizacion: PropTypes.string,
    numero_pre_autorizacion: PropTypes.string,
    codigo_mipres: PropTypes.string,
    origen_direccion: PropTypes.string,
    origen_institucion: PropTypes.string,
    origen_lugar_temporal: PropTypes.string,
    destino_institucion: PropTypes.string,
    destino_lugar_temporal: PropTypes.string,
    origen_ciudad: PropTypes.string,
    origen_departamento: PropTypes.string,
    destino_direccion: PropTypes.string,
    destino_ciudad: PropTypes.string,
    destino_departamento: PropTypes.string,
    inicio_fecha_autorizacion: PropTypes.string,
    final_fecha_autorizacion: PropTypes.string,
    nombre_paciente: PropTypes.string,
    tipo_documento_paciente: PropTypes.string,
    numero_documento_paciente: PropTypes.string,
    telefono_responsable: PropTypes.string,
    empresa: PropTypes.string,
    observaciones: PropTypes.string,
    tipo_traslado: PropTypes.string,
    tipo_viaje: PropTypes.string,
    copago: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    copago_diario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    estado_secundario: PropTypes.string,
    estado_terciario: PropTypes.string,
    estado_cuarto: PropTypes.string,
    fallidos: PropTypes.number,
    cancelados: PropTypes.number,
    observaciones_count: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCreateNovedad: PropTypes.func.isRequired,
  onHistorialNovedades: PropTypes.func.isRequired,
  getBorderColor: PropTypes.func.isRequired,
  onHistorialAutorizacion: PropTypes.func.isRequired,
  onVersiones: PropTypes.func,
  onObservacionesAutorizacion: PropTypes.func,
  hasPermission: PropTypes.func.isRequired,
  numero_entrega: PropTypes.number,
  entregas_autorizadas: PropTypes.number,
};

export default AuthorizationCard;
